import axios from 'axios';
import { from, forkJoin, lastValueFrom, Observable, of, shareReplay } from 'rxjs';
import { map, switchMap, catchError } from 'rxjs/operators';
import {
  IQueryRepository,
  FeatureEntity,
  LayerFieldEntity,
  ResponseValueObject,
  StatisticDefinition,
  LayerStatisticEntity,
  ExtentEntity,
  LayerEntity,
  LayerTypeEnum,
  QueryTargetValueObject,
  QueryGeometryFormat,
} from '@gishub/domain';

export class ArcGisQueryRepository implements IQueryRepository {
  /** Metadata de campos por URL de capa, cacheada para evitar reconsultas por búsqueda. */
  private readonly fieldsCache = new Map<string, Observable<LayerFieldEntity[]>>();

  /** Obtiene (y cachea) los campos buscables de un servicio ArcGIS desde su metadata. */
  private layerFields(url: string): Observable<LayerFieldEntity[]> {
    let cached = this.fieldsCache.get(url);
    if (!cached) {
      cached = from(
        axios.get(url, { params: { f: 'json' }, timeout: 30_000 })
      ).pipe(
        map((response) => {
          const raw = (response.data?.fields ?? []) as { name?: string; type?: string }[];
          // Campos candidatos a alojar un código/número/título: texto y GUID
          // (códigos/nombres) y numéricos (números de expediente/registro). El
          // tipo se conserva para construir el `where` adecuado por campo.
          return raw
            .filter((f) => {
              const t = f?.type;
              return t === 'esriFieldTypeString' || t === 'esriFieldTypeGUID' || t === 'esriFieldTypeInteger' || t === 'esriFieldTypeSmallInteger' || t === 'esriFieldTypeDouble' || t === 'esriFieldTypeSingle' || t === 'esriFieldTypeLong' || t === 'esriFieldTypeBigInteger';
            })
            .map((f) => ({ name: f!.name!, kind: f?.type === 'esriFieldTypeString' || f?.type === 'esriFieldTypeGUID' ? 'text' : 'number' }))
            .filter((f): f is LayerFieldEntity => !!f.name);
        }),
        shareReplay(1)
      );
      this.fieldsCache.set(url, cached);
    }
    return cached;
  }

  /**
   * Detecta los campos a usar para filtrar el valor ingresado.
   * 1. Prefiere los candidatos explícitos que existan entre los campos buscables.
   * 2. Si no hay coincidencia, usa todos los campos buscables de la capa. Si el
   *    valor parece numérico se priorizan los numéricos (código de expediente),
   *    y si es alfanumérico, los de texto (código/nombre), incluyendo el resto
   *    como complemento para no perder coincidencias.
   */
  private resolveFilterFields(available: LayerFieldEntity[], candidates: string[], numericValue: boolean): LayerFieldEntity[] {
    const explicit = available.filter((f) => candidates.includes(f.name));
    if (explicit.length > 0) return explicit;
    const ordered = [...available].sort((a, b) => {
      const an = a.kind === 'number' ? 1 : 0;
      const bn = b.kind === 'number' ? 1 : 0;
      return numericValue ? an - bn : bn - an;
    });
    return ordered;
  }

  /** Construye el where para filtrar por código sobre un conjunto de campos. */
  private buildAoiWhere(fields: LayerFieldEntity[], value: string): string {
    const safe = value.replace(/'/g, "''");
    const numeric = /^-?\d[\d.,]*$/.test(value.trim());
    const clauses = fields.map((f) => {
      if (numeric && f.kind === 'number') {
        return `(${f.name} = ${safe.replace(',', '.')})`;
      }
      return `UPPER(${f.name}) LIKE UPPER('%${safe}%')`;
    });
    return `(${clauses.join(' OR ')})`;
  }

  /**
   * Consulta una capa con los campos buscables descubiertos en su metadata.
   * `format` controla la geometría de la respuesta: 'json' (esriJSON, default)
   * o 'geojson' (FeatureCollection GeoJSON para AOI/buscadores por código).
   */
  private queryTarget(
    target: QueryTargetValueObject,
    value: string,
    format: QueryGeometryFormat = 'json'
  ): Observable<ResponseValueObject<FeatureEntity[]>> {
    return this.layerFields(target.url).pipe(
      switchMap((available) => {
        const usable = target.fields?.length
          ? this.resolveFilterFields(available, target.fields, /^-?\d[\d.,]*$/.test(value.trim()))
          : available;
        if (usable.length === 0) {
          return of(new ResponseValueObject(false, [], `La capa no expone campos buscables para consultar por código`));
        }
        return from(this.queryLayer(target.url, this.buildAoiWhere(usable, value), '*', true, undefined, format)).pipe(
          map((result) =>
            result.success
              ? result
              : new ResponseValueObject(false, [], `Consulta fallida en ${target.url}`)
          )
        );
      }),
      catchError(() => of(new ResponseValueObject(false, [], `Metadata no disponible en ${target.url}`)))
    );
  }

  async queryLayer(url: string, where = '1=1', outFields = '*', returnGeometry = false, outSR?: number, format: QueryGeometryFormat = 'json'): Promise<ResponseValueObject<FeatureEntity[]>> {
    try {
      const params: Record<string, any> = { where, outFields, returnGeometry, f: format === 'geojson' ? 'geojson' : 'json' };
      if (outSR != null) params.outSR = outSR;
      const response = await axios.get(`${url}/query`, {
        params,
        timeout: 120_000,
      });
      const features = format === 'geojson'
        ? (response.data?.features ?? []).map((f: any) => ({
            properties: f.properties ?? {},
            geometry: f.geometry,
          }))
        : (response.data?.features ?? []).map((f: any) => ({
            attributes: f.attributes,
            geometry: f.geometry,
          }));
      return new ResponseValueObject(true, features);
    } catch (err: any) {
      return new ResponseValueObject(false, [], err?.message ?? 'Error querying ArcGIS layer');
    }
  }

  async queryLayerByGeometry(
    url: string,
    geometry: any,
    geometryType = 'esriGeometryPolygon',
    where = '1=1',
    outFields = '*',
    outSR?: number,
    inSR?: number
  ): Promise<ResponseValueObject<FeatureEntity[]>> {
    try {
      let geomStr = '';
      if (typeof geometry === 'string') {
        geomStr = geometry;
      } else if (geometry?.rings) {
        geomStr = JSON.stringify({ rings: geometry.rings, spatialReference: geometry.spatialReference ?? { wkid: inSR ?? 4326 } });
      } else {
        geomStr = JSON.stringify(geometry);
      }

      const params: Record<string, any> = {
        where,
        geometry: geomStr,
        geometryType,
        spatialRel: 'esriSpatialRelIntersects',
        inSR: inSR ?? geometry?.spatialReference?.wkid ?? 4326,
        outFields,
        returnGeometry: true,
        f: 'json',
      };
      if (outSR != null) params.outSR = outSR;

      const response = await axios.post(`${url}/query`, new URLSearchParams(params), {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        timeout: 60_000,
      });

      const raw = response.data as { features?: { attributes: Record<string, any>; geometry?: any }[] };
      const features: FeatureEntity[] = (raw.features ?? []).map((f) => ({
        attributes: f.attributes,
        geometry: f.geometry,
      }));
      return new ResponseValueObject(true, features);
    } catch (err: any) {
      return new ResponseValueObject(false, [], err?.message ?? 'Error querying ArcGIS layer by geometry');
    }
  }

  async queryByPoint(
    entity: LayerEntity,
    point: { x: number; y: number; spatialReference: { wkid: number } }
  ): Promise<ResponseValueObject<FeatureEntity[]>> {
    try {
      switch (entity.type) {
        case LayerTypeEnum.FeatureServer:
        case LayerTypeEnum.SubServer:
        case LayerTypeEnum.MapServer:
          return await this._queryFeatureLayerByPoint(entity.url!, point);
        case LayerTypeEnum.WMS:
          return await this._queryWMSLayerByPoint(entity, point);
        default:
          return new ResponseValueObject(true, []);
      }
    } catch (err: any) {
      return new ResponseValueObject(false, [], err?.message ?? 'Error querying by point');
    }
  }

  private async _queryFeatureLayerByPoint(
    url: string,
    point: { x: number; y: number; spatialReference: { wkid: number } },
  ): Promise<ResponseValueObject<FeatureEntity[]>> {
    const params: Record<string, any> = {
      geometry: JSON.stringify({ x: point.x, y: point.y }),
      geometryType: 'esriGeometryPoint',
      spatialRel: 'esriSpatialRelIntersects',
      distance: 10,
      units: 'esriSRUnit_Meter',
      inSR: point.spatialReference.wkid,
      outFields: '*',
      returnGeometry: false,
      f: 'json',
    };
    const response = await axios.post(`${url}/query`, new URLSearchParams(params), { timeout: 30_000 });
    const raw = response.data as { features?: { attributes: Record<string, any> }[] };
    const features: FeatureEntity[] = (raw.features ?? []).map((f) => ({ attributes: f.attributes }));
    return new ResponseValueObject(true, features);
  }
  private async _queryWMSLayerByPoint(
    entity: LayerEntity,
    point: { x: number; y: number; spatialReference: { wkid: number } },
  ): Promise<ResponseValueObject<FeatureEntity[]>> {
    // Implementation for querying WMS layers by point
    return new ResponseValueObject(true, []);
  }

  async queryWMSFeatureInfo(
    url: string,
    options: {
      layers: string;
      bbox: string;
      width: number;
      height: number;
      x: number;
      y: number;
      version?: string;
      spatialReference: { wkid: number };
    },
  ): Promise<ResponseValueObject<FeatureEntity[]>> {
    try {
      const version = options.version ?? '1.3.0';
      const wkid = options.spatialReference.wkid;
      const crsKey = version.startsWith('1.3') ? 'CRS' : 'SRS';
      const params: Record<string, any> = {
        SERVICE: 'WMS',
        VERSION: version,
        REQUEST: 'GetFeatureInfo',
        LAYERS: options.layers,
        QUERY_LAYERS: options.layers,
        STYLES: '',
        [crsKey]: `EPSG:${wkid}`,
        BBOX: options.bbox,
        WIDTH: options.width,
        HEIGHT: options.height,
        I: options.x,
        J: options.y,
        INFO_FORMAT: 'application/json',
        FEATURE_COUNT: 50,
        F: 'json',
      };
      const response = await axios.get(url, { params, timeout: 30_000 });
      const raw = response.data as {
        features?: any[];
        type?: string;
      };
      const rawFeatures = Array.isArray(raw?.features) ? raw.features : raw?.type === 'Feature' ? [raw] : [];
      const features: FeatureEntity[] = rawFeatures
        .map((f) => ({ attributes: f.properties ?? {}, geometry: f.geometry ?? undefined }))
        .filter((f) => f.attributes && Object.keys(f.attributes).length > 0);
      return new ResponseValueObject(true, features);
    } catch (err: any) {
      return new ResponseValueObject(false, [], err?.message ?? 'Error querying WMS by point');
    }
  }

  async queryLayerStatistics(
    url: string,
    where = '1=1',
    outStatistics?: StatisticDefinition[],
    groupByFields?: string[],
    geometry?: any,
    geometryType?: string,
    spatialRel?: string,
  ): Promise<ResponseValueObject<LayerStatisticEntity[]>> {
    try {
      const params: Record<string, any> = { where, f: 'json' };

      if (outStatistics && outStatistics.length > 0) {
        params.outStatistics = JSON.stringify(outStatistics);
      }
      if (groupByFields && groupByFields.length > 0) {
        params.groupByFieldsForStatistics = groupByFields.join(',');
      }
      if (geometry) {
        params.geometry = typeof geometry === 'string' ? geometry : JSON.stringify(geometry);
        params.geometryType = geometryType ?? 'esriGeometryEnvelope';
        params.spatialRel = spatialRel ?? 'esriSpatialRelIntersects';
        // inSR SIEMPRE deriva de la geometría (nunca asume 4326): el mapa
        // opera en webMercator y un envelope con SR equivocado consulta
        // coordenadas que no existen.
        params.inSR = String(geometry?.spatialReference?.wkid ?? geometry?.spatialReference?.latestWkid ?? 4326);
      }

      const usePost = !!geometry;
      const response = usePost
        ? await axios.post(`${url}/query`, new URLSearchParams(params), { timeout: 120_000 })
        : await axios.get(`${url}/query`, { params, timeout: 120_000 });
      const raw = response.data as { features?: { attributes: Record<string, any> }[] };
      const stats: LayerStatisticEntity[] = (raw.features ?? []).map((f) => f.attributes);
      return new ResponseValueObject(true, stats);
    } catch (err: any) {
      return new ResponseValueObject(false, [], err?.message ?? 'Error querying ArcGIS layer statistics');
    }
  }

  async queryLayerCount(
    url: string,
    where = '1=1',
    geometry?: any,
    geometryType?: string,
    spatialRel?: string,
  ): Promise<ResponseValueObject<number>> {
    try {
      const params: Record<string, any> = { where, returnCountOnly: true, f: 'json' };
      if (geometry) {
        params.geometry = typeof geometry === 'string' ? geometry : JSON.stringify(geometry);
        params.geometryType = geometryType ?? 'esriGeometryEnvelope';
        params.spatialRel = spatialRel ?? 'esriSpatialRelIntersects';
        // inSR SIEMPRE deriva de la geometría (nunca asume 4326).
        params.inSR = String(geometry?.spatialReference?.wkid ?? geometry?.spatialReference?.latestWkid ?? 4326);
      }
      const usePost = !!geometry;
      const response = usePost
        ? await axios.post(`${url}/query`, new URLSearchParams(params), { timeout: 120_000 })
        : await axios.get(`${url}/query`, { params, timeout: 120_000 });
      const count = (response.data as { count?: number }).count ?? 0;
      return new ResponseValueObject(true, count);
    } catch (err: any) {
      return new ResponseValueObject(false, 0, err?.message ?? 'Error counting layer features');
    }
  }

  async queryLayerExtent(url: string, where = '1=1'): Promise<ResponseValueObject<ExtentEntity | null>> {
    try {
      const response = await axios.get(`${url}/query`, {
        params: { where, returnExtentOnly: true, f: 'json' },
        timeout: 120_000,
      });
      const data = response.data as { extent?: ExtentEntity };
      return new ResponseValueObject(true, data.extent ?? null);
    } catch (err: any) {
      return new ResponseValueObject(false, null, err?.message ?? 'Error getting layer extent');
    }
  }

  /**
   * Busca características por código sobre uno o varios servicios ArcGIS en
   * paralelo y combina los resultados. Por cada capa se verifican los campos
   * candidatos contra su metadata (cacheada) para solo filtrar por campos
   * realmente existentes. Devuelve un único objeto limpio (Promise).
   * `format` controla el formato de la geometría: 'geojson' para AOI (default
   * de este buscador por código) o 'json' (esriJSON) para compatibilidad.
   */
  async queryAoiByCodes(
    targets: QueryTargetValueObject[],
    value: string,
    format: QueryGeometryFormat = 'geojson'
  ): Promise<ResponseValueObject<FeatureEntity[]>> {
    if (!targets?.length) {
      return new ResponseValueObject(false, [], 'No se especificaron servicios para la búsqueda');
    }
    const combined = forkJoin(targets.map((target) => this.queryTarget(target, value, format))).pipe(
      map((results) => {
        const errors = results.filter((r) => !r.success).map((r) => r.message ?? '').filter(Boolean);
        const features = results.flatMap((r) => (r.success ? (r.data ?? []) : []));
        if (features.length === 0 && errors.length > 0) {
          return new ResponseValueObject(false, [], errors[0], errors);
        }
        return new ResponseValueObject(true, features, undefined, errors);
      })
    );
    try {
      return await lastValueFrom(combined);
    } catch {
      return new ResponseValueObject(false, [], 'Error al consultar los servicios.');
    }
  }
}
